import { useMutation, useQueryClient } from "@tanstack/vue-query";
import ContactTag from "@/services/contact/contact-tag";
import type { RegisterContactTag } from "@/dtos/ContactTag.dto";

export interface RegisterContactTagParams {
  contactIds: string[];
  tagId: string;
  organizationId: string;
}

/**
 * Composable for contact tag mutations
 */
export function useContactTags() {
  const queryClient = useQueryClient();

  const registerMutation = useMutation({
    mutationFn: async (params: RegisterContactTagParams) => {
      const payload: RegisterContactTag = {
        contactIds: params.contactIds,
        tagId: params.tagId,
        organizationId: params.organizationId,
      };
      const result = await ContactTag.registerContactTag(payload);
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to register contact tag");
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (contactTagId: string) => {
      const result = await ContactTag.deleteContactTag(contactTagId);
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to delete contact tag");
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  return {
    registerContactTag: registerMutation.mutateAsync,
    isRegistering: registerMutation.isPending,
    registerError: registerMutation.error,
    deleteContactTag: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  };
}
